// apps/vidu-next/src/app/utils/get-audio-metadata.ts
/**
 * 音频元数据接口
 */
export interface AudioMetadata {
  /** 音频时长（秒） */
  duration: number;
}

/**
 * 获取音频元数据配置选项
 */
export interface GetAudioMetadataOptions {
  /** 超时时间（毫秒），默认为 10000ms */
  timeout?: number;
}

/**
 * 获取音频的元数据信息（时长）
 * @param audioUrl - 音频 URL
 * @param options - 配置选项
 * @returns Promise<AudioMetadata> 包含音频的时长
 */
export const getAudioMetadata = async (
  audioUrl: string,
  options: GetAudioMetadataOptions = {},
): Promise<AudioMetadata> => {
  const { timeout = 10000 } = options;

  return new Promise<AudioMetadata>((resolve, reject) => {
    const audio = document.createElement('audio');
    let timeoutId: NodeJS.Timeout | null = null;

    const cleanup = () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // 清理 audio 元素
      audio.src = '';
      audio.load();
    };

    const handleLoadedMetadata = () => {
      try {
        const duration = audio.duration;

        if (!duration || Number.isNaN(duration) || duration <= 0) {
          cleanup();
          reject(new Error('无法获取音频时长'));
          return;
        }

        cleanup();
        resolve({
          duration,
        });
      } catch (error) {
        cleanup();
        reject(new Error(`获取音频元数据失败: ${error instanceof Error ? error.message : '未知错误'}`));
      }
    };

    const handleError = (event: Event) => {
      cleanup();
      reject(new Error(`音频加载失败: ${event.type}`));
    };

    // 设置超时机制
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('获取音频元数据超时'));
    }, timeout);

    audio.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
    audio.addEventListener('error', handleError, { once: true });

    // 设置音频源并触发加载
    audio.preload = 'metadata';
    audio.src = audioUrl;
  });
};
